import clsx from "clsx"
import { useRef } from "react"

import useInfiniteRowVirtualizer from "@hooks/useInfiniteRowVirtualizer"
import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useRouter from "@hooks/useRouter"

import isModKeyPressed from "@utils/isModKeyPressed"
import { ifSpaceOrEnter } from "@utils/keyboard"

import styles from "./CardList.module.scss"
import { CardListProps } from "./CardList.types"

// Initial row-height estimate (card + gap); the virtualizer measures each rendered card and corrects it.
const CARD_HEIGHT = 178

export default function CardList(props: CardListProps) {
    const {
        rows,
        hasNextPage = false,
        isFetchingNextPage = false,
        fetchNextPage,
        getRowRoute,
        onRowHoverChange,
        loaderCard,
        children: renderCard,
    } = props

    const isRowClickable = getRowRoute !== undefined

    const router = useRouter()
    const { isAnyDialogOpen } = useIsAnyDialogOpen()

    const scrollingElement = useRef<HTMLDivElement>(null)

    const { rowVirtualizer, virtualRows } = useInfiniteRowVirtualizer({
        rowCount: rows.length,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        getScrollElement: () => scrollingElement.current,
        estimatedRowHeight: CARD_HEIGHT,
        overscan: 5,
    })

    const onCardClick = (detailsURL: string | undefined, event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (detailsURL) {
            if (isModKeyPressed(event)) {
                window.open(detailsURL, "_blank", "noopener,noreferrer")
            } else {
                router.push(detailsURL)
            }
        }
    }

    return (
        <div ref={scrollingElement} className={styles.scrollContainer}>
            <div className={styles.virtualizer} style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {virtualRows.map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > rows.length - 1

                    if (isLoaderRow && hasNextPage) {
                        return (
                            <div
                                key={virtualRow.index}
                                className={styles.cardContainer}
                                style={{ transform: `translateY(${virtualRow.start}px)` }}
                                data-index={virtualRow.index}
                                ref={rowVirtualizer.measureElement}
                                aria-busy={true}
                            >
                                {loaderCard}
                            </div>
                        )
                    }

                    const rowData = rows[virtualRow.index]
                    const detailsURL = getRowRoute?.(rowData)
                    const cardId = String(rowData.id)

                    return (
                        <div
                            key={cardId}
                            className={clsx(styles.cardContainer, { [styles.clickable]: isRowClickable })}
                            style={{ transform: `translateY(${virtualRow.start}px)` }}
                            onClick={(event) => onCardClick(detailsURL, event)}
                            onKeyDown={(event) => ifSpaceOrEnter(event, () => onCardClick(detailsURL))}
                            tabIndex={isAnyDialogOpen ? -1 : 0}
                            id={cardId}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            onMouseEnter={() => onRowHoverChange?.(cardId)}
                        >
                            {renderCard(rowData)}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
